import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Badge } from "@components/ui/badge";
export interface FixtureInvariantMetaProps {
  id: string;
  name: string;
  goal: string;
  preconditions: string;
  inputData: string[];
  expectedResult: string;
  edgeCases: string;
}

/**
 * A component for rendering invariant metadata in fixture documentation.
 * Displays structured information about test invariants including id, name, goal,
 * preconditions, input data, expected results, and edge cases.
 *
 * @param {Object} props - The props for the component.
 * @param {string} props.id - The unique identifier for the invariant.
 * @param {string} props.name - The human-readable name of the invariant.
 * @param {string} props.goal - The purpose/goal of this invariant test.
 * @param {string} props.preconditions - The preconditions required for this test.
 * @param {string[]} props.inputData - Array of input data descriptions.
 * @param {string} props.expectedResult - The expected result of the test.
 * @param {string} props.edgeCases - Description of edge cases or "Нет" if none.
 *
 * @returns {JSX.Element} A formatted metadata block for the invariant.
 */
export const FixtureInvariantMeta: React.FC<FixtureInvariantMetaProps> = t0 => {
  const $ = _c(33);
  const {
    id,
    name,
    goal,
    preconditions,
    inputData,
    expectedResult,
    edgeCases
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = <h4 className="font-semibold underline decoration-1 underline-offset-2">{name}</h4>;
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== id) {
    t2 = <Badge variant="secondary" className="ml-auto">{id}</Badge>;
    $[2] = id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="font-medium text-foreground">Цель:</span>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== goal) {
    t5 = <div>{t4}{" "}<span className="text-muted-foreground">{goal}</span></div>;
    $[8] = goal;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="font-medium text-foreground">Предусловия:</span>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== preconditions) {
    t7 = <div>{t6}{" "}<span className="text-muted-foreground">{preconditions}</span></div>;
    $[11] = preconditions;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="font-medium text-foreground">Входные данные:</span>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== inputData) {
    t9 = inputData.map(_temp);
    $[14] = inputData;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <div>{t8}<ul className="mt-2 ml-5 list-disc space-y-1 marker:text-muted-foreground">{t9}</ul></div>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="font-medium text-foreground">Ожидаемый результат:</span>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== expectedResult) {
    t12 = <div>{t11}{" "}<span className="text-muted-foreground">{expectedResult}</span></div>;
    $[19] = expectedResult;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="font-medium text-foreground">Крайние случаи:</span>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== edgeCases) {
    t14 = <div>{t13}{" "}<span className="text-muted-foreground">{edgeCases}</span></div>;
    $[22] = edgeCases;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t12 || $[26] !== t14 || $[27] !== t5 || $[28] !== t7) {
    t15 = <div className="grid gap-3">{t5}{t7}{t10}{t12}{t14}</div>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t14;
    $[27] = t5;
    $[28] = t7;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t3) {
    t16 = <div className="flex flex-col gap-4 space-y-4 rounded-md border bg-accent/5 p-5 text-sm md:text-base">{t3}{t15}</div>;
    $[30] = t15;
    $[31] = t3;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
};
function _temp(data, index) {
  return <li key={index} className="pl-1 text-muted-foreground">{data}</li>;
}